import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertTriangle, Database, Layers, MoreHorizontal, RefreshCw, Trash2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { AdminPage, EmptyState, PillTabs } from '../../../components/design-system/admin';
import { useQueueAdminApi } from '../../../modules/superadmin/queue-monitor/use-queue-admin-api.hook';
import { DELETE_ALL_PHRASE, HOURS_OPTIONS } from '../../../modules/superadmin/queue-monitor/queue-monitor.types';
import type {
    CleanupDays,
    HoursFilter,
    QueueStats,
    QueueType,
    StatusFilter,
} from '../../../modules/superadmin/queue-monitor/queue-monitor.types';
import { filterJobs, getStatusCount } from '../../../modules/superadmin/queue-monitor/queue-monitor.utils';
import { QueueKpis } from '../../../modules/superadmin/queue-monitor/QueueKpis';
import { QueueBatchesTable } from '../../../modules/superadmin/queue-monitor/QueueBatchesTable';
import { QueueJobsTable } from '../../../modules/superadmin/queue-monitor/QueueJobsTable';
import { JobDetailsSheet } from '../../../modules/superadmin/queue-monitor/JobDetailsSheet';
import {
    CancelBatchDialog,
    CleanupJobsDialog,
    DeleteAllJobsDialog,
    RetryBatchDialog,
} from '../../../modules/superadmin/queue-monitor/QueueActionDialogs';

/** The stats refresh on their own this often. */
export const QUEUE_REFRESH_MS = 30000;

const QUEUE_NAME_KEY: Record<QueueType, string> = {
    'bulk-products': 'ADMIN_QUEUE.QUEUE.BULK',
    seamless: 'ADMIN_QUEUE.QUEUE.SEAMLESS',
};

const HOURS_KEY: Record<HoursFilter, string> = {
    1: 'ADMIN_QUEUE.RANGE.H1',
    6: 'ADMIN_QUEUE.RANGE.H6',
    24: 'ADMIN_QUEUE.RANGE.H24',
    48: 'ADMIN_QUEUE.RANGE.H48',
    168: 'ADMIN_QUEUE.RANGE.D7',
    720: 'ADMIN_QUEUE.RANGE.D30',
};

const STATUS_FILTERS: StatusFilter[] = ['all', 'active', 'completed', 'failed', 'waiting', 'stuck'];

/**
 * Where to open: `?queue=seamless&hours=720&status=waiting` — so the overview's
 * "jobs stuck" can land on exactly those jobs. Anything unknown falls back.
 */
const initialView = (params: URLSearchParams) => {
    const hours = Number(params.get('hours')) as HoursFilter;
    const status = params.get('status') as StatusFilter;
    return {
        queue: (params.get('queue') === 'seamless' ? 'seamless' : 'bulk-products') as QueueType,
        hours: HOURS_OPTIONS.includes(hours) ? hours : (24 as HoursFilter),
        status: STATUS_FILTERS.includes(status) ? status : ('all' as StatusFilter),
    };
};

/** A count the API reports back, when it does. */
const countOf = (res: any, field: string): number | null => (typeof res?.[field] === 'number' ? res[field] : null);

/**
 * Job queues: the background work behind product imports and seamless
 * textures — what is waiting, running, done or failing, per batch and per job,
 * with retry, cancel, replay and clean-up. Refreshes itself every 30 seconds.
 */
export function QueueMonitorPage() {
    const { t, i18n } = useTranslation();
    const api = useQueueAdminApi();
    const [searchParams] = useSearchParams();
    const [initial] = useState(() => initialView(searchParams));

    const [stats, setStats] = useState<QueueStats | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(false);
    const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
    const [expandedBatch, setExpandedBatch] = useState<string | null>(null);
    // Opened on a status (the overview's links), the jobs come first: batches start folded.
    const [batchesSectionExpanded, setBatchesSectionExpanded] = useState(initial.status === 'all');
    const [hoursFilter, setHoursFilter] = useState<HoursFilter>(initial.hours);
    const [showCleanupDialog, setShowCleanupDialog] = useState(false);
    const [showRetryDialog, setShowRetryDialog] = useState(false);
    const [showDeleteAllDialog, setShowDeleteAllDialog] = useState(false);
    const [deleteAllConfirmText, setDeleteAllConfirmText] = useState('');
    const [selectedBatchForRetry, setSelectedBatchForRetry] = useState<string | null>(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [selectedQueue, setSelectedQueue] = useState<QueueType>(initial.queue);
    const [statusFilter, setStatusFilter] = useState<StatusFilter>(initial.status);
    const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
    const [jobDetails, setJobDetails] = useState<any | null>(null);
    const [jobDetailsLoading, setJobDetailsLoading] = useState(false);
    const [showJobDetailsDialog, setShowJobDetailsDialog] = useState(false);
    const [replayingJobId, setReplayingJobId] = useState<string | null>(null);
    const [confirmCancelBatch, setConfirmCancelBatch] = useState<string | null>(null);
    const [cancellingBatch, setCancellingBatch] = useState<string | null>(null);

    // Only the latest request may land: a slow answer for the queue (or range)
    // just left must not overwrite the one now on screen.
    const requestSeq = useRef(0);

    const fetchStats = useCallback(async () => {
        const seq = ++requestSeq.current;
        setIsLoading(true);
        setError(false);
        try {
            const data = await api.getStats(selectedQueue, hoursFilter);
            if (seq !== requestSeq.current) return;
            setStats(data ?? null);
            setLastRefresh(new Date());
        } catch (err: any) {
            if (seq !== requestSeq.current) return;
            setError(true);
            toast.error(t('ADMIN_QUEUE.TOAST.LOAD_FAILED'), { id: 'queue-stats-error' });
            console.error('Error fetching queue stats:', err);
        } finally {
            if (seq === requestSeq.current) setIsLoading(false);
        }
        // `t` is left out on purpose: a language switch must not refetch.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hoursFilter, selectedQueue, api]);

    useEffect(() => {
        fetchStats();
        const interval = setInterval(fetchStats, QUEUE_REFRESH_MS);
        return () => clearInterval(interval);
    }, [fetchStats]);

    const changeQueue = (queue: QueueType) => {
        if (queue === selectedQueue) return;
        // The columns follow the queue: don't show one queue's rows under the other's.
        setStats(null);
        setExpandedBatch(null);
        setSelectedQueue(queue);
    };

    const handleRetryBatch = async () => {
        if (!selectedBatchForRetry) return;
        setActionLoading(true);
        try {
            const res = await api.retryFailed(selectedBatchForRetry);
            setShowRetryDialog(false);
            setSelectedBatchForRetry(null);
            const count = countOf(res, 'retriedCount');
            toast.success(count === null ? t('ADMIN_QUEUE.TOAST.RETRY_DONE') : t('ADMIN_QUEUE.TOAST.RETRIED', { count }));
            fetchStats();
        } catch (err) {
            console.error('Error retrying batch:', err);
            toast.error(t('ADMIN_QUEUE.TOAST.RETRY_FAILED'));
        } finally {
            setActionLoading(false);
        }
    };

    const handleCancelBatch = async () => {
        if (!confirmCancelBatch) return;
        setCancellingBatch(confirmCancelBatch);
        try {
            await api.abortBatch(confirmCancelBatch);
            setConfirmCancelBatch(null);
            toast.success(t('ADMIN_QUEUE.TOAST.CANCEL_DONE'));
            fetchStats();
        } catch (err) {
            console.error('Error aborting batch:', err);
            toast.error(t('ADMIN_QUEUE.TOAST.CANCEL_FAILED'));
        } finally {
            setCancellingBatch(null);
        }
    };

    const handleCleanup = async (days: CleanupDays) => {
        setActionLoading(true);
        try {
            const res = await api.cleanup(selectedQueue, days);
            setShowCleanupDialog(false);
            const count = countOf(res, 'deletedCount');
            toast.success(count === null ? t('ADMIN_QUEUE.TOAST.CLEANUP_DONE') : t('ADMIN_QUEUE.TOAST.DELETED', { count }));
            fetchStats();
        } catch (err) {
            console.error('Error cleaning up queue:', err);
            toast.error(t('ADMIN_QUEUE.TOAST.CLEANUP_FAILED'));
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteAll = async () => {
        if (deleteAllConfirmText !== DELETE_ALL_PHRASE) return;
        setActionLoading(true);
        try {
            const res = await api.deleteAll(selectedQueue);
            setShowDeleteAllDialog(false);
            setDeleteAllConfirmText('');
            const count = countOf(res, 'deletedCount');
            toast.success(count === null ? t('ADMIN_QUEUE.TOAST.DELETE_ALL_DONE') : t('ADMIN_QUEUE.TOAST.DELETED', { count }));
            fetchStats();
        } catch (err) {
            console.error('Error deleting all jobs:', err);
            toast.error(t('ADMIN_QUEUE.TOAST.DELETE_ALL_FAILED'));
        } finally {
            setActionLoading(false);
        }
    };

    const handleViewJobDetails = async (jobId: string) => {
        setSelectedJobId(jobId);
        setJobDetails(null);
        setJobDetailsLoading(true);
        setShowJobDetailsDialog(true);
        try {
            const data = await api.getJob(jobId, selectedQueue);
            setJobDetails(data);
        } catch (err) {
            // Said in the sheet itself ("Couldn't load this job").
            console.error('Error fetching job details:', err);
            setJobDetails(null);
        } finally {
            setJobDetailsLoading(false);
        }
    };

    // The job stays in state while the sheet slides out (so it doesn't flash
    // "couldn't load"); opening the next one replaces it.
    const closeJobDetails = () => setShowJobDetailsDialog(false);

    const handleReplayJob = async (jobId: string) => {
        setReplayingJobId(jobId);
        try {
            await api.replayJob(jobId, selectedQueue);
            toast.success(t('ADMIN_QUEUE.TOAST.REPLAY_DONE'));
            fetchStats();
        } catch (err) {
            console.error('Error replaying job:', err);
            toast.error(t('ADMIN_QUEUE.TOAST.REPLAY_FAILED'));
        } finally {
            setReplayingJobId(null);
        }
    };

    const queueName = t(QUEUE_NAME_KEY[selectedQueue]);
    // Stuck jobs come as their own list: a long range can push them out of the 100 most recent.
    const filteredJobs = !stats ? [] : statusFilter === 'stuck' ? stats.stuckJobs ?? [] : filterJobs(stats.recentJobs, statusFilter);
    const counts: Record<StatusFilter, number> = {
        all: getStatusCount(stats, 'all'),
        active: getStatusCount(stats, 'active'),
        waiting: getStatusCount(stats, 'waiting'),
        completed: getStatusCount(stats, 'completed'),
        failed: getStatusCount(stats, 'failed'),
        stuck: getStatusCount(stats, 'stuck'),
    };

    return (
        <AdminPage
            title={t('ADMIN_SHELL.NAV.QUEUES')}
            description={t('ADMIN_QUEUE.DESCRIPTION')}
            actions={
                <>
                    {lastRefresh && (
                        <span className="text-xs text-muted-foreground tabular-nums" data-testid="queue-updated">
                            {t('ADMIN_QUEUE.UPDATED', {
                                time: lastRefresh.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' }),
                            })}
                        </span>
                    )}
                    <Button variant="outline" size="icon-sm" onClick={fetchStats} disabled={isLoading} aria-label={t('ADMIN_QUEUE.REFRESH')}>
                        <RefreshCw className={cn(isLoading && 'animate-spin')} />
                    </Button>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="icon-sm" aria-label={t('ADMIN_QUEUE.MORE_ACTIONS')}>
                                <MoreHorizontal />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-auto">
                            <DropdownMenuItem onSelect={() => setShowCleanupDialog(true)}>
                                <Trash2 />
                                {t('ADMIN_QUEUE.CLEANUP_MENU')}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem variant="destructive" onSelect={() => setShowDeleteAllDialog(true)}>
                                <AlertTriangle />
                                {t('ADMIN_QUEUE.DELETE_ALL_MENU')}
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </>
            }
        >
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex flex-wrap items-center gap-3">
                    <PillTabs<QueueType>
                        value={selectedQueue}
                        onChange={changeQueue}
                        ariaLabel={t('ADMIN_QUEUE.QUEUE_LABEL')}
                        options={[
                            { value: 'bulk-products', label: t(QUEUE_NAME_KEY['bulk-products']), icon: <Database /> },
                            { value: 'seamless', label: t(QUEUE_NAME_KEY.seamless), icon: <Layers /> },
                        ]}
                    />
                    {stats && (
                        <span className="text-xs text-muted-foreground" data-testid="queue-mode">
                            {stats.serviceBusEnabled ? t('ADMIN_QUEUE.MODE.SERVICE_BUS') : t('ADMIN_QUEUE.MODE.LOCAL')}
                        </span>
                    )}
                </div>
                <PillTabs<HoursFilter>
                    value={hoursFilter}
                    onChange={setHoursFilter}
                    ariaLabel={t('ADMIN_QUEUE.RANGE_LABEL')}
                    options={HOURS_OPTIONS.map((h) => ({ value: h, label: t(HOURS_KEY[h]) }))}
                />
            </div>

            {error && !stats && !isLoading ? (
                <EmptyState
                    title={t('ADMIN_SHELL.COMMON.LOAD_FAILED')}
                    action={
                        <Button variant="outline" onClick={fetchStats}>
                            {t('ADMIN_SHELL.COMMON.RETRY')}
                        </Button>
                    }
                    className="py-24"
                />
            ) : (
                <>
                    <QueueKpis stats={stats} />

                    {stats ? (
                        <>
                            {stats.batchSummaries.length > 0 && (
                                <QueueBatchesTable
                                    batches={stats.batchSummaries}
                                    jobs={stats.recentJobs}
                                    queue={selectedQueue}
                                    expanded={batchesSectionExpanded}
                                    onToggleExpanded={() => setBatchesSectionExpanded(!batchesSectionExpanded)}
                                    expandedBatch={expandedBatch}
                                    onToggleBatch={(id) => setExpandedBatch(expandedBatch === id ? null : id)}
                                    cancellingBatch={cancellingBatch}
                                    onRetryFailed={(id) => {
                                        setSelectedBatchForRetry(id);
                                        setShowRetryDialog(true);
                                    }}
                                    onCancelBatch={setConfirmCancelBatch}
                                />
                            )}
                            <QueueJobsTable
                                jobs={filteredJobs}
                                queue={selectedQueue}
                                statusFilter={statusFilter}
                                onStatusFilterChange={setStatusFilter}
                                counts={counts}
                                replayingJobId={replayingJobId}
                                cancellingBatch={cancellingBatch}
                                onViewDetails={handleViewJobDetails}
                                onReplay={handleReplayJob}
                                onCancelBatch={setConfirmCancelBatch}
                            />
                        </>
                    ) : (
                        <>
                            <Skeleton className="h-40 rounded-xl" />
                            <Skeleton className="h-96 rounded-xl" />
                        </>
                    )}
                </>
            )}

            <RetryBatchDialog
                open={showRetryDialog}
                onOpenChange={(open) => !open && setShowRetryDialog(false)}
                busy={actionLoading}
                onConfirm={handleRetryBatch}
            />

            <CancelBatchDialog
                open={!!confirmCancelBatch}
                onOpenChange={(open) => !open && setConfirmCancelBatch(null)}
                busy={!!cancellingBatch}
                onConfirm={handleCancelBatch}
            />

            <CleanupJobsDialog
                open={showCleanupDialog}
                onOpenChange={(open) => !open && setShowCleanupDialog(false)}
                queueName={queueName}
                busy={actionLoading}
                onConfirm={handleCleanup}
            />

            <DeleteAllJobsDialog
                open={showDeleteAllDialog}
                onOpenChange={(open) => {
                    if (!open) {
                        setShowDeleteAllDialog(false);
                        setDeleteAllConfirmText('');
                    }
                }}
                queueName={queueName}
                value={deleteAllConfirmText}
                onValueChange={setDeleteAllConfirmText}
                busy={actionLoading}
                onConfirm={handleDeleteAll}
            />

            <JobDetailsSheet
                open={showJobDetailsDialog}
                jobId={selectedJobId}
                details={jobDetails}
                loading={jobDetailsLoading}
                queue={selectedQueue}
                replaying={!!selectedJobId && replayingJobId === selectedJobId}
                onReplay={() => {
                    handleReplayJob(selectedJobId!);
                    closeJobDetails();
                }}
                onClose={closeJobDetails}
            />
        </AdminPage>
    );
}
